import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { Phone, Mail, MapPin, Video, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Droit des sociétés",
  "Conformité bancaire & LCB-FT/KYC",
  "Conformité numérique (RGPD, AI Act)",
  "Formation & sensibilisation",
  "Autre demande",
];

const CONTACTS = [
  {
    icon: Phone,
    label: "Téléphone",
    value: "+33 6 21 13 30 49",
    href: "tel:+33621133049",
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "E-mail",
    value: "lehilamugobe@gmail.com",
    href: "mailto:lehilamugobe@gmail.com",
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Adresse postale",
    value: "138 avenue Victor Hugo, 75016 Paris",
    href: "https://www.google.com/maps/search/?api=1&query=138+avenue+Victor+Hugo+75016+Paris",
    testid: "contact-address-link",
  },
  {
    icon: Video,
    label: "Interventions",
    value: "En présentiel et à distance",
    testid: "contact-intervention-item",
  },
];

const inputCls =
  "w-full border border-ink-line bg-ink px-4 py-3.5 text-sm text-ivory placeholder:text-mute/60 outline-none transition-colors duration-300 focus:border-gold/70";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    topic: TOPICS[0],
    message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onDevis = (e) => {
      const { size, need, topic } = e.detail || {};
      setForm((f) => ({
        ...f,
        topic: topic && TOPICS.includes(topic) ? topic : f.topic,
        message: `Bonjour,\n\nJe souhaite un devis personnalisé.\nStructure : ${size}\nBesoin : ${need}\n\n`,
      }));
    };
    window.addEventListener("alc:devis", onDevis);
    return () => window.removeEventListener("alc:devis", onDevis);
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.email_sent) {
        toast.success("Votre message a bien été envoyé. Vous serez recontacté(e) rapidement.");
      } else {
        toast.warning(
          "Message enregistré, mais la notification e-mail est momentanément indisponible. N'hésitez pas à appeler directement."
        );
      }
      setForm({ name: "", email: "", company: "", topic: TOPICS[0], message: "" });
    } catch (err) {
      toast.error(
        err.response?.data?.detail
          ? "Une erreur est survenue. Vérifiez les champs du formulaire."
          : "L'envoi a échoué. Réessayez ou contactez-nous par téléphone."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden border-t border-ink-line py-24 lg:py-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 bottom-0 h-[50vh] w-[50vh] rounded-full bg-gold/5 blur-[130px]"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              Discutons de vos{" "}
              <em className="italic text-gold">enjeux juridiques</em>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-mute">
              Décrivez votre besoin en quelques lignes : chaque demande reçoit
              une réponse personnalisée.
            </p>
          </Reveal>
          <div className="mt-10 space-y-3">
            {CONTACTS.map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-ink-line text-gold transition-colors duration-300 group-hover:border-gold/60">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-mute">
                      {c.label}
                    </span>
                    <span className="mt-1 block text-sm text-ivory sm:text-base">
                      {c.value}
                    </span>
                  </span>
                </>
              );
              const cls =
                "group flex items-center gap-5 border border-ink-line bg-ink-card/50 px-6 py-5 transition-colors duration-300 hover:border-gold/50";
              return (
                <Reveal key={c.label} delay={0.1 + i * 0.08}>
                  {c.href ? (
                    <a
                      href={c.href}
                      data-testid={c.testid}
                      target={c.label === "Adresse postale" ? "_blank" : undefined}
                      rel={c.label === "Adresse postale" ? "noopener noreferrer" : undefined}
                      className={cls}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={c.testid} className={cls}>
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-ink-line bg-ink-card p-7 sm:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
                  Nom complet *
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Votre nom"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
                  E-mail *
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="vous@entreprise.fr"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
                  Société / structure
                </label>
                <input
                  id="contact-company"
                  data-testid="contact-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Nom de votre structure"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-topic" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
                  Sujet *
                </label>
                <select
                  id="contact-topic"
                  data-testid="contact-topic-select"
                  value={form.topic}
                  onChange={set("topic")}
                  className={`${inputCls} appearance-none`}
                >
                  {TOPICS.map((t) => (
                    <option key={t} value={t} className="bg-ink-card">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
                Votre message *
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                minLength={10}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Décrivez votre besoin, votre structure et vos échéances…"
                className={`${inputCls} resize-y`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={sending}
              className="group mt-7 flex w-full items-center justify-center gap-3 bg-gold px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-ivory disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Envoi en cours…
                </>
              ) : (
                <>
                  Envoyer ma demande
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p className="mt-5 text-xs leading-relaxed text-mute/70">
              Les informations transmises via ce formulaire sont utilisées
              uniquement pour répondre à votre demande (voir mentions légales &
              politique de confidentialité).
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
